import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { CheckCircle, PlugsConnected, Plugs } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function MetaCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('connecting');
  const [error, setError] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [pages, setPages] = useState([]);
  const [adAccounts, setAdAccounts] = useState([]);
  const [selectedPage, setSelectedPage] = useState('');
  const [selectedAdAccount, setSelectedAdAccount] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    
    if (!code || !state) {
      navigate('/dashboard');
      return;
    }

    const processCallback = async () => {
      try {
        setStatus('exchanging');
        const response = await axios.post(
          `${API_URL}/api/meta/callback?code=${code}&state=${state}`
        );
        
        const { access_token, pages: pgs, ad_accounts: accs } = response.data;
        
        if (!pgs?.length || !accs?.length) {
          setError('Nessuna pagina o account pubblicitario trovato. Assicurati di avere una pagina Facebook e un account Meta Ads.');
          setStatus('error');
          return;
        }

        setAccessToken(access_token);
        setPages(pgs);
        setAdAccounts(accs);
        setSelectedPage(pgs[0].id);
        setSelectedAdAccount(accs[0].id);

        // If only one of each, auto-save
        if (pgs.length === 1 && accs.length === 1) {
          await saveConnection(access_token, pgs[0].id, accs[0].id, pgs[0].name);
        } else {
          setStatus('select');
        }
      } catch (err) {
        console.error('Meta callback error:', err);
        setError(err.response?.data?.detail || 'Errore nella connessione a Meta. Riprova.');
        setStatus('error');
      }
    };

    processCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveConnection = async (token, pageId, adAccountId, pageName) => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/meta/connect`, {
        access_token: token,
        page_id: pageId,
        ad_account_id: adAccountId,
        page_name: pageName
      });

      await refreshUser();
      setStatus('success');
      
      const wizardState = localStorage.getItem('vaionline_wizard_state');
      if (wizardState) {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        navigate(`/create?code=${code}&state=${state}`);
      } else {
        setTimeout(() => navigate('/dashboard'), 1500);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nel salvataggio. Riprova.');
      setStatus('error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSelection = () => {
    const selectedPageData = pages.find(p => p.id === selectedPage);
    saveConnection(accessToken, selectedPage, selectedAdAccount, selectedPageData?.name);
  };

  return (
    <div className="min-h-screen bg-[#05050A] flex items-center justify-center px-4">
      <div className="text-center max-w-md w-full">
        {(status === 'connecting' || status === 'exchanging') && (
          <>
            <div className="w-12 h-12 border-2 border-white/20 border-t-[#0055FF] rounded-full animate-spin mx-auto mb-4" />
            <p className="text-white font-semibold mb-1">Collegamento in corso...</p>
            <p className="text-[#A1A1AA] text-sm">Non chiudere questa pagina</p>
          </>
        )}

        {status === 'select' && (
          <div className="bg-[#0C0D14] border border-white/10 rounded-2xl p-6 text-left">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#0055FF]/20 flex items-center justify-center">
                <Plugs weight="fill" className="w-5 h-5 text-[#0055FF]" />
              </div>
              <div>
                <h2 className="font-bold text-lg" style={{ fontFamily: 'Unbounded' }}>Seleziona Account</h2>
                <p className="text-sm text-[#A1A1AA]">Scegli la pagina e l'ad account da usare</p>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <div className="space-y-2">
                <Label className="text-sm text-[#A1A1AA]">Pagina Facebook</Label>
                <select
                  data-testid="meta-page-select"
                  value={selectedPage}
                  onChange={(e) => setSelectedPage(e.target.value)}
                  className="w-full px-3 py-2.5 bg-black border border-white/10 rounded-lg text-white focus:border-[#0055FF] focus:outline-none"
                >
                  {pages.map((page) => (
                    <option key={page.id} value={page.id}>{page.name}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm text-[#A1A1AA]">Ad Account</Label>
                <select
                  data-testid="meta-ad-account-select"
                  value={selectedAdAccount}
                  onChange={(e) => setSelectedAdAccount(e.target.value)}
                  className="w-full px-3 py-2.5 bg-black border border-white/10 rounded-lg text-white focus:border-[#0055FF] focus:outline-none"
                >
                  {adAccounts.map((acc) => (
                    <option key={acc.id} value={acc.id}>{acc.name || acc.id}</option>
                  ))}
                </select>
              </div>
            </div>

            <Button
              data-testid="save-meta-selection"
              onClick={handleSaveSelection}
              disabled={saving || !selectedPage || !selectedAdAccount}
              className="w-full bg-[#0055FF] hover:bg-[#0066FF] text-white font-semibold"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Salvataggio...
                </span>
              ) : (
                <>
                  <PlugsConnected weight="fill" className="w-4 h-4 mr-2" />
                  Conferma e Collega
                </>
              )}
            </Button>
          </div>
        )}

        {status === 'success' && (
          <>
            <div className="w-12 h-12 rounded-full bg-[#E4FF1A]/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle weight="fill" className="w-6 h-6 text-[#E4FF1A]" />
            </div>
            <p className="text-white font-semibold mb-1">Meta connesso!</p>
            <p className="text-[#A1A1AA] text-sm">Reindirizzamento...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-white font-semibold mb-2">Errore di connessione</p>
            <p className="text-[#A1A1AA] text-sm mb-4">{error}</p>
            <button
              data-testid="back-to-dashboard"
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white text-sm transition-colors"
            >
              Torna alla Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
}
